import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  DownloadVibeAppPackageQuerySchema,
  DownloadVibeAppStarterQuerySchema,
  GetVibeClusterResponseSchema,
  GetVibeGitProjectCredentialsPathVarsSchema,
  GetVibeGitProjectCredentialsResponseSchema,
  ProvisionVibeClusterBodySchema,
  ProvisionVibeClusterResponseSchema
} from "../schemas";

export const VibeClusterEndpoints = {
  // ---------------------------------------------------------------------------
  // Tenant cluster — org-scoped health read
  // ---------------------------------------------------------------------------

  /**
   * Fetch the org's dedicated tenant-cluster health: lifecycle status, the
   * customer-safe condition (fault / routine update pending / nominal, with a
   * plain-language sentence), and the git host's and telemetry's convergence
   * statuses. The console renders its persistent cluster banners from this.
   *
   * `cluster: null` (200, not 404) when the org has no dedicated cluster —
   * absence is the normal shared-infrastructure state, not a failure.
   */
  GetCluster: {
    method: "GET" as const,
    path: "/api/vibe/cluster",
    Response: ApiSuccessSchema(GetVibeClusterResponseSchema)
  },

  /**
   * Opt the caller's own org into a dedicated cluster. Declarative: it
   * records desired state (a PROVISIONING row) and returns immediately — the
   * reconcile loop converges the stacks over the following tens of minutes,
   * and the console tracks progress by polling GetCluster.
   *
   * Idempotent: a second call while PROVISIONING, or against an already
   * serving cluster, is a no-op that reports the current status rather than
   * an error. A retired cluster (disabled or destroyed) is revived in place.
   */
  ProvisionCluster: {
    method: "POST" as const,
    path: "/api/vibe/cluster/provision",
    Body: ProvisionVibeClusterBodySchema,
    Response: ApiSuccessSchema(ProvisionVibeClusterResponseSchema)
  },

  /**
   * Download the org app starter as ONE self-contained `.tgz`: the
   * `@agent-nexus/apps-starter` content with `@agent-nexus/apps-ui` vendored
   * under `vendor/` and wired as a `file:` dependency. Both packages are
   * private; the backend fetches them with the platform's own registry
   * credential, which never leaves the server — so `npm install` on the
   * extracted bundle needs no npm token.
   *
   * Entries live under `package/`, like any npm tarball. The resolved versions
   * are echoed in the `x-nexus-apps-starter-version` and
   * `x-nexus-apps-ui-version` response headers.
   */
  DownloadAppStarter: {
    method: "GET" as const,
    path: "/api/vibe/app-starter",
    Query: DownloadVibeAppStarterQuerySchema,
    responseType: "blob" as const
  },

  /**
   * Download ONE private `@agent-nexus/*` package as its published `.tgz`, so
   * an app that ALREADY EXISTS on a customer's machine can vendor it.
   *
   * `DownloadAppStarter` above serves a whole new app and therefore covers only
   * the first command of a build. Every moment after it — adding the library to
   * an app scaffolded some other way, upgrading it, re-installing after a
   * clone, and `npm ci` in the customer's own CI — resolves the package FROM
   * THE REGISTRY, which 404s on any machine holding no credential for our
   * private scope. This route closes those.
   *
   * The response is the published tarball verbatim, so its bytes hash to the
   * digest the registry published and a lockfile built from them is honest. The
   * resolved name, version and integrity are echoed in the
   * `x-nexus-app-package-*` response headers.
   */
  DownloadAppPackage: {
    method: "GET" as const,
    path: "/api/vibe/app-package",
    Query: DownloadVibeAppPackageQuerySchema,
    responseType: "blob" as const
  },

  /**
   * Fetch a push credential bound to ONE git project — the only git push
   * credential any route serves. The tenant's ORG-WIDE token, which pushes to
   * every repository, stays on the server
   * (`apps/backend/src/__governance__/org-git-credential-is-never-served.spec.ts`).
   *
   * The credential is a per-project machine user with `write` on this
   * repository alone. 404 if the project does not exist in the caller's org.
   * 409 while the agent has not yet provisioned + reported the credential
   * (retryable — the same converge loop that materializes the repo mints it).
   * The response `pushToken` is a live secret.
   */
  GetGitProjectCredentials: {
    method: "GET" as const,
    path: "/api/vibe/git-projects/:id/credentials",
    PathVars: GetVibeGitProjectCredentialsPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeGitProjectCredentialsResponseSchema)
  }
} as const;
